import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { staleGuard } from "src/utils/staleGuard";

// ─────────────────────────────────────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  isLoading: false,
  error: null,

  // Ingredientes
  ingredientes: [],
  ingredienteActivo: null,

  // Proveedores
  proveedores: [],
  proveedorActivo: null,

  // Elaboraciones
  elaboraciones: [],

  // Receta activa (producto o elaboración)
  recetaActiva: null,

  // Órdenes de compra
  ordenesCompra: [],
  ordenCompraActiva: null,

  // Albaranes
  albaranes: [],
  albaranActivo: null,

  // Stock
  stockActual: [],
  movimientos: [],

  // Inventarios
  inventarios: [],
  inventarioActivo: null,

  // Dashboard y alertas
  dashboard: null,
  alertas: null,

  // OCR albarán
  ocrResult: null,
  ocrLoading: false,

  // Sugerencias de compra
  sugerencias: [],
  sugerenciasLoading: false,

  // Rentabilidad
  rentabilidad: null,
  rentabilidadLoading: false,

  // Evolución
  evolucion: null,
  evolucionLoading: false,

  // Insights
  insights: null,
  insightsLoading: false,

  // Balance
  balance: null,
  balanceLoading: false,

  // Stock por producto (productoId → unidades disponibles)
  stockProductos: {},

  // 2026-10 · Inventario y costes (hub)
  articulos: [],
  articulosLoading: false,
  recetasEstado: [],
  recetasEstadoLoading: false,
  puestaEnMarcha: null,
  alertasResumen: null,
  teoricoVsReal: null,
  teoricoVsRealLoading: false,
  // 2026-10 · Fase 2 · Incidencias con proveedores
  incidencias: [],
  incidenciasLoading: false,

  // Escandallo IA (wizard)
  escandalloIA: {
    isAnalyzing: false,
    isApplying: false,
    sugerencia: null,
    error: null,
    resultado: null,
  },
};

const slice = createSlice({
  name: "gestion",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Ingredientes
    setIngredientes(state, action) {
      state.isLoading = false;
      state.ingredientes = action.payload;
    },
    setIngredienteActivo(state, action) {
      state.isLoading = false;
      state.ingredienteActivo = action.payload;
    },
    upsertIngrediente(state, action) {
      state.isLoading = false;
      const idx = state.ingredientes.findIndex((i) => i.id === action.payload.id);
      if (idx >= 0) state.ingredientes[idx] = action.payload;
      else state.ingredientes.push(action.payload);
    },
    removeIngrediente(state, action) {
      state.isLoading = false;
      state.ingredientes = state.ingredientes.filter((i) => i.id !== action.payload);
    },

    // Proveedores
    setProveedores(state, action) {
      state.isLoading = false;
      state.proveedores = action.payload;
    },
    setProveedorActivo(state, action) {
      state.isLoading = false;
      state.proveedorActivo = action.payload;
    },
    upsertProveedor(state, action) {
      state.isLoading = false;
      const idx = state.proveedores.findIndex((p) => p.id === action.payload.id);
      if (idx >= 0) state.proveedores[idx] = action.payload;
      else state.proveedores.push(action.payload);
    },
    removeProveedor(state, action) {
      state.isLoading = false;
      state.proveedores = state.proveedores.filter((p) => p.id !== action.payload);
    },

    // Elaboraciones
    setElaboraciones(state, action) {
      state.isLoading = false;
      state.elaboraciones = action.payload;
    },
    upsertElaboracion(state, action) {
      state.isLoading = false;
      const idx = state.elaboraciones.findIndex((e) => e.id === action.payload.id);
      if (idx >= 0) state.elaboraciones[idx] = action.payload;
      else state.elaboraciones.push(action.payload);
    },

    // Receta
    setRecetaActiva(state, action) {
      state.isLoading = false;
      state.recetaActiva = action.payload;
    },

    // Órdenes de compra
    setOrdenesCompra(state, action) {
      state.isLoading = false;
      state.ordenesCompra = action.payload;
    },
    setOrdenCompraActiva(state, action) {
      state.isLoading = false;
      state.ordenCompraActiva = action.payload;
    },
    upsertOrdenCompra(state, action) {
      state.isLoading = false;
      const idx = state.ordenesCompra.findIndex((o) => o.id === action.payload.id);
      if (idx >= 0) state.ordenesCompra[idx] = action.payload;
      else state.ordenesCompra.unshift(action.payload);
    },

    // Albaranes
    setAlbaranes(state, action) {
      state.isLoading = false;
      state.albaranes = action.payload;
    },
    setAlbaranActivo(state, action) {
      state.isLoading = false;
      state.albaranActivo = action.payload;
    },
    upsertAlbaran(state, action) {
      state.isLoading = false;
      const idx = state.albaranes.findIndex((a) => a.id === action.payload.id);
      if (idx >= 0) state.albaranes[idx] = action.payload;
      else state.albaranes.unshift(action.payload);
    },

    // Stock
    setStockActual(state, action) {
      state.isLoading = false;
      state.stockActual = action.payload;
    },
    setMovimientos(state, action) {
      state.isLoading = false;
      state.movimientos = action.payload;
    },

    // Inventarios
    setInventarios(state, action) {
      state.isLoading = false;
      state.inventarios = action.payload;
    },
    setInventarioActivo(state, action) {
      state.isLoading = false;
      state.inventarioActivo = action.payload;
    },
    upsertInventario(state, action) {
      state.isLoading = false;
      const idx = state.inventarios.findIndex((i) => i.id === action.payload.id);
      if (idx >= 0) state.inventarios[idx] = action.payload;
      else state.inventarios.unshift(action.payload);
    },

    // Dashboard
    setDashboard(state, action) {
      state.isLoading = false;
      state.dashboard = action.payload;
    },
    setAlertas(state, action) {
      state.isLoading = false;
      state.alertas = action.payload;
    },

    // OCR
    startOcrLoading(state) {
      state.ocrLoading = true;
      state.ocrResult = null;
    },
    setOcrResult(state, action) {
      state.ocrLoading = false;
      state.ocrResult = action.payload;
    },
    clearOcrResult(state) {
      state.ocrLoading = false;
      state.ocrResult = null;
    },

    // Sugerencias de compra
    startSugerenciasLoading(state) {
      state.sugerenciasLoading = true;
    },
    setSugerencias(state, action) {
      state.sugerenciasLoading = false;
      state.sugerencias = action.payload;
    },

    // Rentabilidad
    startRentabilidadLoading(state) {
      state.rentabilidadLoading = true;
    },
    setRentabilidad(state, action) {
      state.rentabilidadLoading = false;
      state.rentabilidad = action.payload;
    },
    startEvolucionLoading(state) {
      state.evolucionLoading = true;
    },
    setEvolucion(state, action) {
      state.evolucionLoading = false;
      state.evolucion = action.payload;
    },
    startInsightsLoading(state) {
      state.insightsLoading = true;
    },
    setInsights(state, action) {
      state.insightsLoading = false;
      state.insights = action.payload;
    },
    startBalanceLoading(state) {
      state.balanceLoading = true;
    },
    setBalance(state, action) {
      state.balanceLoading = false;
      state.balance = action.payload;
    },
    setStockProductos(state, action) {
      state.stockProductos = action.payload;
    },

    // Inventario y costes (hub)
    startArticulosLoading(state) {
      state.articulosLoading = true;
    },
    setArticulos(state, action) {
      state.articulosLoading = false;
      state.articulos = action.payload || [];
    },
    upsertArticulo(state, action) {
      const a = action.payload;
      if (!a) return;
      const idx = state.articulos.findIndex((x) => x.id === a.id && x.tipo === a.tipo);
      if (idx >= 0) state.articulos[idx] = a;
      else state.articulos.push(a);
    },
    startRecetasEstadoLoading(state) {
      state.recetasEstadoLoading = true;
    },
    setRecetasEstado(state, action) {
      state.recetasEstadoLoading = false;
      state.recetasEstado = action.payload || [];
    },
    setPuestaEnMarcha(state, action) {
      state.puestaEnMarcha = action.payload;
    },
    setAlertasResumen(state, action) {
      state.alertasResumen = action.payload;
    },
    startTeoricoVsRealLoading(state) {
      state.teoricoVsRealLoading = true;
    },
    setTeoricoVsReal(state, action) {
      state.teoricoVsRealLoading = false;
      state.teoricoVsReal = action.payload;
    },
    startIncidenciasLoading(state) {
      state.incidenciasLoading = true;
    },
    setIncidencias(state, action) {
      state.incidenciasLoading = false;
      state.incidencias = action.payload || [];
    },
    upsertIncidencia(state, action) {
      const i = action.payload;
      if (!i) return;
      const idx = state.incidencias.findIndex((x) => x.id === i.id);
      if (idx >= 0) state.incidencias[idx] = i;
      else state.incidencias.unshift(i);
    },

    // Escandallo IA
    startEscandalloIAAnalyzing(state) {
      state.escandalloIA.isAnalyzing = true;
      state.escandalloIA.error = null;
      state.escandalloIA.sugerencia = null;
    },
    setEscandalloIASugerencia(state, action) {
      state.escandalloIA.isAnalyzing = false;
      state.escandalloIA.sugerencia = action.payload;
    },
    updateEscandalloIASugerencia(state, action) {
      state.escandalloIA.sugerencia = action.payload;
    },
    startEscandalloIAApplying(state) {
      state.escandalloIA.isApplying = true;
      state.escandalloIA.error = null;
    },
    setEscandalloIAResultado(state, action) {
      state.escandalloIA.isApplying = false;
      state.escandalloIA.resultado = action.payload;
    },
    escandalloIAError(state, action) {
      state.escandalloIA.isAnalyzing = false;
      state.escandalloIA.isApplying = false;
      state.escandalloIA.error = action.payload;
    },
    resetEscandalloIA(state) {
      state.escandalloIA = {
        isAnalyzing: false,
        isApplying: false,
        sugerencia: null,
        error: null,
        resultado: null,
      };
    },
  },
});

export const {
  startLoading, hasError,
  setIngredientes, setIngredienteActivo, upsertIngrediente, removeIngrediente,
  setProveedores, setProveedorActivo, upsertProveedor, removeProveedor,
  setElaboraciones, upsertElaboracion,
  setRecetaActiva,
  setOrdenesCompra, setOrdenCompraActiva, upsertOrdenCompra,
  setAlbaranes, setAlbaranActivo, upsertAlbaran,
  setStockActual, setMovimientos,
  setInventarios, setInventarioActivo, upsertInventario,
  setDashboard, setAlertas,
  startOcrLoading, setOcrResult, clearOcrResult,
  startSugerenciasLoading, setSugerencias,
  startRentabilidadLoading, setRentabilidad,
  startEvolucionLoading, setEvolucion,
  startInsightsLoading, setInsights,
  startBalanceLoading, setBalance, setStockProductos,
  startArticulosLoading, setArticulos, upsertArticulo,
  startRecetasEstadoLoading, setRecetasEstado, setPuestaEnMarcha, setAlertasResumen,
  startTeoricoVsRealLoading, setTeoricoVsReal,
  startIncidenciasLoading, setIncidencias, upsertIncidencia,
  startEscandalloIAAnalyzing, setEscandalloIASugerencia, updateEscandalloIASugerencia,
  startEscandalloIAApplying, setEscandalloIAResultado, escandalloIAError, resetEscandalloIA,
} = slice.actions;

export default slice.reducer;

// ─────────────────────────────────────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────────────────────────────────────

const BASE = "gestion";

// ── Dashboard ────────────────────────────────────────────────────────────────

export function getDashboard() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/dashboard`);
      dispatch(setDashboard(res.data.dashboard));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getAlertas() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/alertas`);
      dispatch(setAlertas(res.data.alertas));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

// ── Ingredientes ─────────────────────────────────────────────────────────────

export function getIngredientes(all = false) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/ingredientes`, { params: { all } });
      dispatch(setIngredientes(res.data.ingredientes));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getIngrediente(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/ingredientes/${id}`);
      dispatch(setIngredienteActivo(res.data.ingrediente));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function addIngrediente(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/ingredientes`, dto);
      dispatch(upsertIngrediente(res.data.ingrediente));
      return res.data.ingrediente;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function updateIngrediente(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.put(`${BASE}/ingredientes`, dto);
      dispatch(upsertIngrediente(res.data.ingrediente));
      return res.data.ingrediente;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function deleteIngrediente(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      await axios.delete(`${BASE}/ingredientes/${id}`);
      dispatch(removeIngrediente(id));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function ajustarStock(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/ingredientes/ajuste-stock`, dto);
      dispatch(upsertIngrediente(res.data.ingrediente));
      return res.data.ingrediente;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function addIngredienteProveedor(dto) {
  return async (dispatch) => {
    try {
      await axios.post(`${BASE}/ingredientes/proveedor`, dto);
      dispatch(getIngrediente(dto.idIngrediente));
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function updateIngredienteProveedor(dto, idIngrediente) {
  return async (dispatch) => {
    try {
      await axios.put(`${BASE}/ingredientes/proveedor`, dto);
      if (idIngrediente) dispatch(getIngrediente(idIngrediente));
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function removeIngredienteProveedor(id, idIngrediente) {
  return async (dispatch) => {
    try {
      await axios.delete(`${BASE}/ingredientes/proveedor/${id}`);
      dispatch(getIngrediente(idIngrediente));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

// ── Proveedores ──────────────────────────────────────────────────────────────

export function getProveedores(all = false) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/proveedores`, { params: { all } });
      dispatch(setProveedores(res.data.proveedores));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

// Ficha de un proveedor (editar tras recargar: el store llega vacío).
export function getProveedor(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/proveedores/${id}`);
      dispatch(setProveedorActivo(res.data.proveedor));
      return res.data.proveedor;
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function addProveedor(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/proveedores`, dto);
      dispatch(upsertProveedor(res.data.proveedor));
      return res.data.proveedor;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function updateProveedor(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.put(`${BASE}/proveedores`, dto);
      dispatch(upsertProveedor(res.data.proveedor));
      return res.data.proveedor;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function deleteProveedor(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      await axios.delete(`${BASE}/proveedores/${id}`);
      dispatch(removeProveedor(id));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

// ── Elaboraciones ────────────────────────────────────────────────────────────

export function getElaboraciones() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/elaboraciones`);
      dispatch(setElaboraciones(res.data.elaboraciones));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function addElaboracion(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/elaboraciones`, dto);
      dispatch(upsertElaboracion(res.data.elaboracion));
      return res.data.elaboracion;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function updateElaboracion(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.put(`${BASE}/elaboraciones`, dto);
      dispatch(upsertElaboracion(res.data.elaboracion));
      return res.data.elaboracion;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function producirElaboracion(idElaboracion, batches, notes = "") {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/elaboraciones/${idElaboracion}/producir`, {
        batches,
        notes,
      });
      // Refresh elaboracion list to get updated StockActual
      const listRes = await axios.get(`${BASE}/elaboraciones`);
      dispatch(setElaboraciones(listRes.data.elaboraciones));
      return res.data.produccion;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

// ── Recetas ──────────────────────────────────────────────────────────────────

export function getReceta(tipoOrigen, idOrigen) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/receta/${tipoOrigen}/${idOrigen}`);
      dispatch(setRecetaActiva(res.data.receta));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function setReceta(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/receta`, dto);
      dispatch(setRecetaActiva(res.data.receta));
      return res.data.receta;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

// ── Órdenes de compra ────────────────────────────────────────────────────────

export function getSugerenciasCompra({ dias, cobertura } = {}) {
  return async (dispatch) => {
    dispatch(startSugerenciasLoading());
    try {
      const params = {};
      if (dias) params.dias = dias;
      if (cobertura) params.cobertura = cobertura;
      const res = await axios.get(`${BASE}/compras/sugerencias`, { params });
      dispatch(setSugerencias(res.data.sugerencias));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getOrdenesCompra() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/compras/ordenes`);
      dispatch(setOrdenesCompra(res.data.ordenes));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function addOrdenCompra(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/compras/ordenes`, dto);
      dispatch(upsertOrdenCompra(res.data.orden));
      return res.data.orden;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function enviarOrdenCompra(id) {
  return async (dispatch) => {
    try {
      await axios.put(`${BASE}/compras/ordenes/${id}/enviar`);
      const res = await axios.get(`${BASE}/compras/ordenes/${id}`);
      dispatch(upsertOrdenCompra(res.data.orden));
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function cancelarOrdenCompra(id) {
  return async (dispatch) => {
    try {
      await axios.put(`${BASE}/compras/ordenes/${id}/cancelar`);
      const res = await axios.get(`${BASE}/compras/ordenes/${id}`);
      dispatch(upsertOrdenCompra(res.data.orden));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

// ── Albaranes ────────────────────────────────────────────────────────────────

export function getAlbaranes() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/compras/albaranes`);
      dispatch(setAlbaranes(res.data.albaranes));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function addAlbaran(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/compras/albaranes`, dto);
      dispatch(upsertAlbaran(res.data.albaran));
      return res.data.albaran;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function confirmarAlbaran(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.put(`${BASE}/compras/albaranes/${id}/confirmar`);
      dispatch(upsertAlbaran(res.data.albaran));
      // Actualizar stock en Redux tras confirmación
      dispatch(getStockActual());
      return res.data.albaran;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

// ── Stock ────────────────────────────────────────────────────────────────────

export function getStockActual() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/stock/actual`);
      dispatch(setStockActual(res.data.stock));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getMovimientos(idIngrediente, desde, hasta, { idElaboracion, tipo } = {}) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const params = {};
      if (idIngrediente) params.idIngrediente = idIngrediente;
      if (idElaboracion) params.idElaboracion = idElaboracion;
      if (tipo) params.tipo = tipo;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const res = await axios.get(`${BASE}/stock/movimientos`, { params });
      dispatch(setMovimientos(res.data.movimientos));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

// ── Inventarios ──────────────────────────────────────────────────────────────

export function getInventarios() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/stock/inventarios`);
      dispatch(setInventarios(res.data.inventarios));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getInventario(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.get(`${BASE}/stock/inventarios/${id}`);
      dispatch(setInventarioActivo(res.data.inventario));
      return res.data.inventario;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function addInventario(dto) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(`${BASE}/stock/inventarios`, dto);
      dispatch(upsertInventario(res.data.inventario));
      dispatch(setInventarioActivo(res.data.inventario));
      return res.data.inventario;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function updateConteos(id, conteos) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.put(`${BASE}/stock/inventarios/${id}/conteos`, conteos);
      dispatch(upsertInventario(res.data.inventario));
      dispatch(setInventarioActivo(res.data.inventario));
      return res.data.inventario;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function cerrarInventario(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.put(`${BASE}/stock/inventarios/${id}/cerrar`);
      dispatch(upsertInventario(res.data.inventario));
      dispatch(setInventarioActivo(res.data.inventario));
      dispatch(getStockActual());
      return res.data.inventario;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

// ── OCR albarán ──────────────────────────────────────────────────────────────

export function ocrAlbaran(file) {
  return async (dispatch) => {
    dispatch(startOcrLoading());
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${BASE}/compras/albaranes/ocr`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(setOcrResult(res.data.ocr));
      return res.data.ocr;
    } catch (e) {
      dispatch(clearOcrResult());
      throw e;
    }
  };
}

// ── Rentabilidad ─────────────────────────────────────────────────────────────

export function getStockProductos({ force = false } = {}) {
  return async (dispatch) => {
    // Ya viene en el bootstrap del TPV. Los llamadores locales
    // (dispatch(getStockProductos()) en useEffect al entrar a una mesa)
    // reciben cache hit durante el TTL.
    if (!force && staleGuard("gestion/stock-productos")) return;
    try {
      const res = await axios.get(`${BASE}/stock-productos`);
      dispatch(setStockProductos(res.data.stockProductos));
    } catch (e) { /* silent — non-critical */ }
  };
}

export function getBalance(desde, hasta) {
  return async (dispatch) => {
    dispatch(startBalanceLoading());
    try {
      const res = await axios.get(`${BASE}/balance`, { params: { desde, hasta } });
      dispatch(setBalance(res.data.balance));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getInsights() {
  return async (dispatch) => {
    dispatch(startInsightsLoading());
    try {
      const res = await axios.get(`${BASE}/insights`);
      dispatch(setInsights(res.data.insights));
    } catch (e) { dispatch(hasError(e.message)); }
  };
}

export function getEvolucion(desde, hasta) {
  return async (dispatch) => {
    dispatch(startEvolucionLoading());
    try {
      const res = await axios.get(`${BASE}/evolucion`, { params: { desde, hasta } });
      dispatch(setEvolucion(res.data.evolucion));
      return res.data.evolucion;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

export function getRentabilidad(desde, hasta) {
  return async (dispatch) => {
    dispatch(startRentabilidadLoading());
    try {
      const res = await axios.get(`${BASE}/rentabilidad`, { params: { desde, hasta } });
      dispatch(setRentabilidad(res.data.rentabilidad));
      return res.data.rentabilidad;
    } catch (e) { dispatch(hasError(e.message)); throw e; }
  };
}

// ── Inventario y costes (hub) ────────────────────────────────────────────────

export function getArticulos(tipo) {
  return async (dispatch) => {
    dispatch(startArticulosLoading());
    try {
      const res = await axios.get(`${BASE}/articulos`, { params: tipo ? { tipo } : {} });
      dispatch(setArticulos(res.data.articulos));
      return res.data.articulos;
    } catch (e) {
      dispatch(setArticulos([]));
      dispatch(hasError(e.message));
      return [];
    }
  };
}

/** Ajuste, merma o consumo interno de un artículo (ingrediente o elaboración). */
export function ajustarStockArticulo(body) {
  return async (dispatch) => {
    const res = await axios.post(`${BASE}/stock/ajuste`, body);
    if (res.data.articulo) dispatch(upsertArticulo(res.data.articulo));
    dispatch(getAlertasResumen());
    return res.data.articulo;
  };
}

export function getRecetasEstado() {
  return async (dispatch) => {
    dispatch(startRecetasEstadoLoading());
    try {
      const res = await axios.get(`${BASE}/recetas/estado`);
      dispatch(setRecetasEstado(res.data.recetas));
      return res.data.recetas;
    } catch (e) {
      dispatch(setRecetasEstado([]));
      dispatch(hasError(e.message));
      return [];
    }
  };
}

export function setControlStockMasivo(productoIds, activar = true) {
  return async (dispatch) => {
    const res = await axios.post(`${BASE}/recetas/control-stock`, { productoIds, activar });
    dispatch(getRecetasEstado());
    return res.data.actualizados;
  };
}

export function getPuestaEnMarcha() {
  return async (dispatch) => {
    try {
      const res = await axios.get(`${BASE}/puesta-en-marcha`);
      dispatch(setPuestaEnMarcha(res.data.puestaEnMarcha));
      return res.data.puestaEnMarcha;
    } catch (e) {
      return null;
    }
  };
}

export function getAlertasResumen() {
  return async (dispatch) => {
    try {
      const res = await axios.get(`${BASE}/alertas/resumen`);
      dispatch(setAlertasResumen(res.data.resumen));
      return res.data.resumen;
    } catch (e) {
      return null; // sin add-on o sin permiso: sin contador
    }
  };
}

export function recibirOrdenCompra(id) {
  return async (dispatch) => {
    const res = await axios.post(`${BASE}/compras/ordenes/${id}/recibir`);
    dispatch(upsertAlbaran(res.data.albaran));
    return res.data.albaran;
  };
}

export function getAlbaran(id) {
  return async (dispatch) => {
    const res = await axios.get(`${BASE}/compras/albaranes/${id}`);
    dispatch(setAlbaranActivo(res.data.albaran));
    return res.data.albaran;
  };
}

export function getTeoricoVsReal(desde, hasta) {
  return async (dispatch) => {
    dispatch(startTeoricoVsRealLoading());
    try {
      const res = await axios.get(`${BASE}/rentabilidad/teorico-vs-real`, { params: { desde, hasta } });
      dispatch(setTeoricoVsReal(res.data.teoricoVsReal));
      return res.data.teoricoVsReal;
    } catch (e) {
      dispatch(setTeoricoVsReal(null));
      throw e;
    }
  };
}

/** Descarga el PDF de fichas técnicas (ids vacío = todos los platos con receta). */
export async function descargarFichaTecnica(ids = []) {
  const res = await axios.get(`${BASE}/recetas/ficha-tecnica`, {
    params: ids.length ? { ids: ids.join(",") } : {},
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = ids.length === 1 ? `ficha-tecnica-${ids[0]}.pdf` : "fichas-tecnicas.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// ── Fase 2 · Importación CSV ─────────────────────────────────────────────────

export async function descargarPlantillaImportacion(tipo) {
  const res = await axios.get(`${BASE}/importar/plantilla`, { params: { tipo }, responseType: "blob" });
  const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `plantilla-${tipo}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export async function analizarImportacion(tipo, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${BASE}/importar/analizar`, form, {
    params: { tipo },
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.analisis;
}

export async function validarImportacion(body) {
  const res = await axios.post(`${BASE}/importar/validar`, body);
  return res.data.validacion;
}

/** Confirma la importación. Con errores el API responde 422 y devuelve la validación. */
export async function confirmarImportacion(body) {
  try {
    const res = await axios.post(`${BASE}/importar/confirmar`, body);
    return res.data.resultado;
  } catch (e) {
    if (e?.response?.status === 422 && e.response.data?.resultado) return e.response.data.resultado;
    throw e;
  }
}

// ── Fase 2 · Control de compras ──────────────────────────────────────────────

export function getIncidencias(estado = "pendientes", idProveedor) {
  return async (dispatch) => {
    dispatch(startIncidenciasLoading());
    try {
      const params = {};
      if (estado) params.estado = estado;
      if (idProveedor) params.idProveedor = idProveedor;
      const res = await axios.get(`${BASE}/incidencias`, { params });
      dispatch(setIncidencias(res.data.incidencias));
      return res.data.incidencias;
    } catch (e) {
      dispatch(setIncidencias([]));
      return [];
    }
  };
}

export function cambiarEstadoIncidencia(id, body) {
  return async (dispatch) => {
    const res = await axios.put(`${BASE}/incidencias/${id}/estado`, body);
    dispatch(upsertIncidencia(res.data.incidencia));
    dispatch(getAlertasResumen());
    return res.data.incidencia;
  };
}

export async function borradorReclamacion(idProveedor, ids = []) {
  const res = await axios.post(`${BASE}/incidencias/reclamacion`, { idProveedor, ids });
  return res.data.reclamacion;
}

export function marcarReclamadas(ids) {
  return async (dispatch) => {
    const res = await axios.post(`${BASE}/incidencias/reclamadas`, { ids });
    dispatch(getAlertasResumen());
    return res.data.actualizadas;
  };
}

export async function getConfiguracionInventario() {
  const res = await axios.get(`${BASE}/configuracion`);
  return res.data.configuracion;
}

export async function guardarConfiguracionInventario(body) {
  const res = await axios.put(`${BASE}/configuracion`, body);
  return res.data.configuracion;
}

export function registrarFacturaAlbaran(id, body) {
  return async (dispatch) => {
    const res = await axios.put(`${BASE}/compras/albaranes/${id}/factura`, body);
    dispatch(upsertAlbaran(res.data.albaran));
    dispatch(getAlertasResumen());
    return res.data;
  };
}

export async function getKpisProveedor(id) {
  const res = await axios.get(`${BASE}/proveedores/${id}/kpis`);
  return res.data.kpis;
}

export async function getHistorialPrecios(idIngrediente) {
  const res = await axios.get(`${BASE}/ingredientes/${idIngrediente}/precios`);
  return res.data.historial;
}

/**
 * 2026-10 · Precio pactado con el proveedor (relación artículo ↔ proveedor).
 * `precioPactado` null lo quita. Permiso INVENTORY_MANAGE en el servidor.
 */
export async function guardarPrecioPactado(idRelacion, precioPactado, precioPactadoDesde = null) {
  const res = await axios.put(`${BASE}/ingredientes/proveedor/${idRelacion}/precio-pactado`, {
    precioPactado,
    precioPactadoDesde,
  });
  return res.data.relacion;
}

export async function descargarHojaRecuento({ zona, idInventario } = {}) {
  const params = {};
  if (zona) params.zona = zona;
  if (idInventario) params.idInventario = idInventario;
  const res = await axios.get(`${BASE}/stock/hoja-recuento`, { params, responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "hoja-recuento.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// ── Escandallo IA ────────────────────────────────────────────────────────────

export function analizarEscandalloIA(productoIds, incluirComplementos = true) {
  return async (dispatch) => {
    dispatch(startEscandalloIAAnalyzing());
    try {
      const res = await axios.post(`${BASE}/escandallo-ia/analizar`, {
        productoIds,
        incluirComplementos,
      }, { timeout: 240000 });
      dispatch(setEscandalloIASugerencia(res.data.sugerencia));
      return res.data.sugerencia;
    } catch (e) {
      const msg = e?.response?.data || e.message;
      dispatch(escandalloIAError(typeof msg === "string" ? msg : "Error analizando la carta"));
      throw e;
    }
  };
}

export function aplicarEscandalloIA(sugerencia) {
  return async (dispatch) => {
    dispatch(startEscandalloIAApplying());
    try {
      const res = await axios.post(`${BASE}/escandallo-ia/aplicar`, sugerencia);
      dispatch(setEscandalloIAResultado(res.data.result));
      return res.data.result;
    } catch (e) {
      const msg = e?.response?.data || e.message;
      dispatch(escandalloIAError(typeof msg === "string" ? msg : "Error aplicando el escandallo"));
      throw e;
    }
  };
}
